/**
 * Button — `Oprappo Design System.dc.html § 02 নিয়ন্ত্রণ · বাটন`.
 *
 * Frame note: "52px primary, 44px secondary — never below 44. Full-width on mobile:
 * a thumb finds an edge faster than a centre. One primary per screen."
 *
 * Sizes as drawn in the file:
 *   lg  52px  600 16px/1.75   § 02 বাটন, § 04 বটম শিট confirm
 *   md  48px  600 15px/1.75   § 04 মোডাল actions, § 07 খালি অবস্থা action
 *   sm  44px  600 14px/1.75   § 02 বাটন — the "ছোট" pair
 *   inline  padding 10px 14px, 600 14px/1.75   § 06 এআই কার্ড action row
 */
import type { ReactNode } from 'react';
import { cx } from './internal/cx.js';

export type ButtonTone = 'primary' | 'secondary' | 'tertiary' | 'danger';
export type ButtonSize = 'lg' | 'md' | 'sm' | 'inline';

export interface ButtonProps {
  children: ReactNode;
  tone?: ButtonTone | undefined;
  size?: ButtonSize | undefined;
  /**
   * § 02 disabled swatch: `background:#E2DED5; color:#8B9795`.
   * See README § deviations — this is the one place the design paints text in
   * `#8B9795`, which docs/02 otherwise reserves for borders and SVG strokes.
   */
  disabled?: boolean | undefined;
  /** Full-width is the mobile default per the frame note. */
  block?: boolean | undefined;
  type?: 'button' | 'submit' | 'reset' | undefined;
  onClick?: (() => void) | undefined;
  className?: string | undefined;
}

/** Box geometry per size. Heights, not paddings — the design draws fixed heights. */
const SIZE: Record<ButtonSize, string> = {
  lg: 'h-[52px] text-[16px]',
  md: 'h-[48px] text-[15px]',
  sm: 'h-[44px] text-[14px]',
  inline: 'px-[14px] py-[10px] text-[14px]',
};

/**
 * Tone × size. The design draws the 52px secondary with a `1.5px #0E3A45` rule and
 * the 44px secondary with a `1px #E2DED5` rule — two different treatments, both
 * transcribed rather than unified. See README § deviations.
 */
function toneClasses(tone: ButtonTone, size: ButtonSize): string {
  switch (tone) {
    case 'primary':
      return 'bg-teal text-white hover:bg-teal-dark';
    case 'secondary':
      return size === 'sm' || size === 'inline'
        ? 'border border-border text-teal'
        : 'border-[1.5px] border-teal text-teal';
    case 'tertiary':
      return 'text-link';
    case 'danger':
      return 'bg-danger text-white';
  }
}

export function Button({
  children,
  tone = 'primary',
  size = 'lg',
  disabled = false,
  block = true,
  type = 'button',
  onClick,
  className,
}: ButtonProps): JSX.Element {
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      className={cx(
        'inline-flex items-center justify-center font-anek font-semibold leading-[1.75] rounded-none',
        SIZE[size],
        block && size !== 'inline' ? 'w-full' : '',
        disabled ? 'bg-border text-muted border-0 cursor-default' : toneClasses(tone, size),
        className,
      )}
    >
      {children}
    </button>
  );
}
